import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FileSearch, Sparkles, ArrowRight, CheckCircle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import TextBuddyLogo from '@/assets/logos/TextBuddy_Transparent_2.png'
import FoundaDocsLogo from '@/assets/logos/founda-docs-icon-blue.svg'
import { useAuth } from '@/contexts/AuthContext'
import { useUser } from '@/contexts/UserContext'

// Cross-link to the Founda Docs app (separate microservice). Built-in at deploy time per
// environment by the CM frontend workflow; falls back to the prod URL so dev preview builds
// still produce a working link.
const FOUNDA_DOCS_URL =
  import.meta.env.VITE_FOUNDA_DOCS_URL ||
  'https://app-foundaai-prod-fd-frontend.azurewebsites.net'

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
}

export function HomePage() {
  const { isAuthenticated, login } = useAuth()
  const { user } = useUser()

  // Check if user is admin or superuser
  const isAdminOrSuperUser = user && (user.role === 'Admin' || user.role === 'SuperUser')

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-12"
    >
      {/* Hero Section */}
      <motion.section variants={itemVariants} className="text-center py-12">
        <img
          src={TextBuddyLogo}
          alt="TextBuddy"
          className="h-36 w-auto mx-auto mb-6"
        />
        <h1 className="text-4xl font-bold tracking-tight mb-4">
          Review, enhance, and author your knowledge articles
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          AI-powered tools to surface quality issues, rewrite for clarity, and create
          brand-new articles in 20+ languages — straight from your Founda knowledge base.
        </p>
      </motion.section>

      {/* Feature Cards
          - Admin/SuperUser: 3 cards (Evaluate + Enhance + Create in Founda) → md:grid-cols-3
          - Regular user:    2 cards (Enhance + Create in Founda)            → md:grid-cols-2 */}
      <motion.section
        variants={itemVariants}
        className={`grid gap-8 max-w-6xl mx-auto ${isAdminOrSuperUser ? 'md:grid-cols-3' : 'md:grid-cols-2 max-w-4xl'}`}
      >
        {/* Evaluate Card - Only for Admin/SuperUser */}
        {isAdminOrSuperUser && (
          <Card className="relative overflow-hidden group hover:shadow-lg transition-shadow">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <FileSearch className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Evaluate Articles</CardTitle>
              </div>
              <CardDescription>
                Automatically review your knowledge base articles for quality, clarity, and accuracy.
                Identify issues and prioritize improvements.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" />
                  Readability analysis
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" />
                  Content relevancy check
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" />
                  Visual element review
                </li>
              </ul>
              {isAuthenticated ? (
                <Button asChild className="w-full group-hover:bg-primary/90">
                  <Link to="/evaluate">
                    Start Evaluating
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <Button className="w-full" onClick={login}>
                  Sign in to Evaluate
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Enhance Card */}
        <Card className="relative overflow-hidden group hover:shadow-lg transition-shadow">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-transparent pointer-events-none" />
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-secondary/30">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Enhance Articles</CardTitle>
            </div>
            <CardDescription>
              AI-powered content enhancement that rewrites and improves your articles
              while maintaining your brand voice and accuracy.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground mb-6">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-success" />
                Improved clarity & structure
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-success" />
                Enhanced readability
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-success" />
                SEO-optimized meta tags
              </li>
            </ul>
            {isAuthenticated ? (
              <Button asChild variant="secondary" className="w-full">
                <Link to="/enhance">
                  Start Enhancing
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <Button variant="secondary" className="w-full" onClick={login}>
                Sign in to Enhance
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Create in Founda Card — opens the separate Founda Docs app in a new tab */}
        <Card className="relative overflow-hidden group hover:shadow-lg transition-shadow">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <img src={FoundaDocsLogo} alt="Founda Docs" className="h-10 w-10" />
              </div>
              <CardTitle>Create Article in Founda</CardTitle>
            </div>
            <CardDescription>
              Build a brand-new knowledge article from scratch with AI assistance — upload a PDF,
              DOCX, or PPTX and Founda Docs converts it into a structured, branded article ready
              to publish to ServiceNow.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground mb-6">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-success" />
                Upload PDF, DOCX, or PPTX
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-success" />
                AI-generated structured article
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-success" />
                Direct publish to ServiceNow
              </li>
            </ul>
            <Button asChild className="w-full group-hover:bg-primary/90">
              <a href={FOUNDA_DOCS_URL} target="_blank" rel="noopener noreferrer">
                Open Founda Docs
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </motion.section>
    </motion.div>
  )
}
